import React, { JSX, Ref, RefObject, useCallback } from 'react';
import { Col, Row } from 'reactstrap';
import {
  DatePickerRef,
  Error,
  Price,
  ServiceSearchParams,
  useAppService,
  useConfig
} from '@ibe/components';
import { useTranslation } from 'react-i18next';
import Keys from '../../Translations/generated/deal-finder.en.json.keys';
import DateRangeInput from '../Datepicker/DateRangeInput/DateRangeInput';
import MegConfigModel from '../../Config/MegConfigModel';
import { ApiPackageModel, ApiPrice, ApiSeason, ApiTravelDateCandidate } from '@ibe/api';
import dayjs from 'dayjs';
import CmsPackage from '../../Models/CMS/CmsPackage';
import PriceUtil from '../MMB/Util/PriceUtil';
import OffersUtil from '../../Util/OffersUtil';
import DealFinderUtil from '../../Util/DealFinderUtil';
import { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import i18next from 'i18next';
import { DealFinderPaxConfigurationResult } from './DealFinder';
import { HighlightDatesInformation } from '../Datepicker/DateRangeInput/DateRangeInputProps';
import { OfferSettings } from '../../Models/CMS/CmsDealFinderGlobalConfig';

interface DealFinderDatePickerProps {
  packageModel?: CmsPackage;
  startDate?: string;
  endDate?: string;
  searchParams?: Partial<ServiceSearchParams>;
  onChange?: (startDate: string | undefined, endDate: string | undefined) => void;
  isDateNotValid?: boolean;
  isDateRangeNotValid?: boolean;
  minDateRangeSpan?: number;
  maxDateRangeSpan?: number;
  seasons?: ApiSeason[];
  datePickerRef?: RefObject<DatePickerRef>;
  popoverRef?: RefObject<HTMLDivElement | null>;
  dateEditRef?: RefObject<HTMLElement>;
  dateRangeInputRef?: Ref<{
    togglePopover: () => void;
    openPopover: () => void;
    closePopover: () => [startDate?: string, endDate?: string];
    getCurrentDateRange: () => [startDate?: string, endDate?: string];
  }>;
  highlightDatesInformation?: HighlightDatesInformation;
  availabilityPackage?: ApiPackageModel;
  onMonthChange?: (date?: Date) => void;
  lowPriceThreshold?: number;
  mediumPriceThreshold?: number;
  calendarRelevantOffers?: OfferSettings[];
  currentlyVisibleMonth?: string;
  dealFinderPaxConfigurationResult?: DealFinderPaxConfigurationResult;
}

const DealFinderDatePicker = (props: DealFinderDatePickerProps) => {
  const {
    packageModel,
    startDate,
    endDate,
    searchParams,
    onChange,
    isDateNotValid,
    isDateRangeNotValid,
    minDateRangeSpan,
    maxDateRangeSpan,
    seasons,
    datePickerRef,
    popoverRef,
    dateEditRef,
    dateRangeInputRef,
    highlightDatesInformation,
    availabilityPackage,
    onMonthChange,
    lowPriceThreshold,
    mediumPriceThreshold,
    calendarRelevantOffers,
    currentlyVisibleMonth,
    dealFinderPaxConfigurationResult
  } = props;

  const { t } = useTranslation('deal-finder');
  const config = useConfig() as MegConfigModel;
  const appService = useAppService();
  const cachedPeriod = packageModel?.dealFinderConfig?.cachedPeriod;
  const dealfinderCacheTimezone = config.dealfinderCacheTimezone;
  const minDay = new Date();
  minDay.setDate(minDay.getDate() + config.datePickerMinDateOffset);

  function isDateRangeOnly2Days(): boolean {
    if (!startDate || !endDate) {
      return false;
    }

    const startOfRange = dayjs(startDate);
    const endOfRange = dayjs(endDate);

    return Math.abs(startOfRange.diff(endOfRange, 'day')) === 1;
  }

  const customFilterDate = useCallback(
    (date: Date) => {
      const testedDate = dayjs(date);

      if (
        (currentlyVisibleMonth && testedDate.isBefore(dayjs(currentlyVisibleMonth), 'month')) ||
        testedDate.isAfter(dayjs(currentlyVisibleMonth as string), 'month')
      ) {
        return true;
      }

      const result = DealFinderUtil.baseCondition(
        date,
        cachedPeriod,
        dealfinderCacheTimezone,
        availabilityPackage?.travelDates
      );

      if (DealFinderUtil.isNotValidDate(startDate) && DealFinderUtil.isNotValidDate(endDate)) {
        return result;
      }

      if (
        !result &&
        startDate &&
        DealFinderUtil.isNotValidDate(endDate) &&
        DealFinderUtil.isDateInRange(date, startDate, maxDateRangeSpan) &&
        DealFinderUtil.allPreviousDatesInRangePassBaseCondition(
          date,
          startDate,
          cachedPeriod,
          dealfinderCacheTimezone
        )
      ) {
        return true;
      } else if (startDate && DealFinderUtil.isNotValidDate(endDate)) {
        const from = dayjs(startDate);
        if (testedDate.isBefore(from)) {
          return false;
        }
        if (
          DealFinderUtil.isDateInRange(date, startDate, maxDateRangeSpan) &&
          DealFinderUtil.allPreviousDatesInRangePassBaseCondition(
            date,
            startDate,
            cachedPeriod,
            dealfinderCacheTimezone
          )
        ) {
          return true;
        }
      }
      return result;
    },
    [
      config.dealfinderCacheTimezone,
      packageModel?.dealFinderConfig?.cachedPeriod,
      availabilityPackage?.travelDates,
      startDate,
      endDate
    ]
  );

  function renderPriceInformation(
    relatedPrice: ApiPrice | undefined,
    date: Date,
    offer: OfferSettings | null
  ): JSX.Element {
    if (
      !relatedPrice ||
      DealFinderUtil.isAfterCachedPeriod(date, cachedPeriod, dealfinderCacheTimezone)
    ) {
      return <div style={{ minHeight: '22px' }} />;
    }

    if (dealFinderPaxConfigurationResult?.isSuccessfullyResolved) {
      return (
        <Row
          className="meg_deal-finder-date-picker_day-price_container"
          style={{
            color: offer?.calendarPriceColor ?? 'inherit'
          }}
        >
          <Col className="meg_deal-finder-date-picker_day-price">
            <Price
              price={relatedPrice}
              roundPrice={config.dealFinderPriceRoundingMode ?? 'DEFAULT_MODE'} //NOSONAR
            />
          </Col>
        </Row>
      );
    }

    return <>{renderPriceIndicator(relatedPrice, offer)}</>;
  }

  function renderPriceIndicator(relatedPrice: ApiPrice, offer: OfferSettings | null): JSX.Element {
    const currencySymbol = PriceUtil.getCurrencySymbol(relatedPrice.currencyCode, appService.lang);

    if (currencySymbol && mediumPriceThreshold && lowPriceThreshold) {
      const indicator = getPriceIndicator(relatedPrice.finalPrice, currencySymbol);
      return (
        <div
          className={'priceIndicator'}
          style={{
            color: offer?.calendarPriceColor ?? 'inherit'
          }}
        >
          {indicator}
        </div>
      );
    }
    return <></>;
  }

  function getPriceIndicator(price: number, symbol: string): string {
    if (price > (mediumPriceThreshold as number)) return symbol.repeat(3);
    if (price <= (lowPriceThreshold as number)) return symbol;
    return symbol.repeat(2);
  }

  function renderOfferInformation(offer: OfferSettings): JSX.Element {
    return (
      <Row className="meg_deal-finder-date-picker_offer_icon-container">
        <Col>
          <img
            className={'meg_deal-finder-date-picker_offer_icon'}
            src={offer?.calendarIcon?.small?.url}
            title={offer?.calendarIcon?.small?.subTitle ?? 'Offer Icon'}
            alt={offer?.calendarIcon?.small?.altText ?? 'Offer Icon'}
          />
        </Col>
      </Row>
    );
  }

  const renderDayContents = useCallback(
    (day: number, date: Date): JSX.Element => {
      const relatedPackageTravelDate:
        | ApiTravelDateCandidate
        | undefined = DealFinderUtil.findPackageTravelDate(date, availabilityPackage?.travelDates);
      const relatedPrice: ApiPrice | undefined = relatedPackageTravelDate?.price;
      let offer: OfferSettings | null = null;
      const dayRelevantOffers: OfferSettings[] = OffersUtil.getDayRelevantOffers(
        date,
        calendarRelevantOffers
      );

      if (dayRelevantOffers.length == 1) {
        offer = dayRelevantOffers[0];
      } else if (dayRelevantOffers.length > 1) {
        offer = OffersUtil.getTopPriorityOffer(dayRelevantOffers);
      }

      return (
        <Row className="meg_deal-finder-date-picker_day-container mb-1 mt-1">
          <Col>
            <Row>
              <Col className="meg_deal-finder-date-picker_day">{day}</Col>
            </Row>
            {offer && relatedPrice ? (
              renderOfferInformation(offer)
            ) : (
              <div style={{ minHeight: '20px' }}> </div>
            )}
            {renderPriceInformation(relatedPrice, date, offer)}
          </Col>
        </Row>
      );
    },
    [
      config.dealfinderCacheTimezone,
      packageModel?.dealFinderConfig?.cachedPeriod,
      availabilityPackage?.travelDates,
      availabilityPackage?.travelDates?.length,
      calendarRelevantOffers,
      calendarRelevantOffers?.length
    ]
  );

  const renderLegends = (): JSX.Element => {
    return (
      <Row>
        {(() => {
          const priceLegendComponents: JSX.Element[] = [];
          const iconLegendComponents: JSX.Element[] = [];

          const monthRelatedOffers = OffersUtil.getMonthRelevantOffers(
            currentlyVisibleMonth,
            calendarRelevantOffers
          );

          monthRelatedOffers?.forEach(offer => {
            priceLegendComponents.push(
              <Col
                className="col-12"
                key={'colorLegend_' + offer.name + offer.calendarPriceColorDescription}
              >
                <div className="d-flex align-items-center">
                  <span
                    className="meg_deal-finder-date-picker_offer_price-legend"
                    style={{
                      color: offer?.calendarPriceColor ?? 'inherit'
                    }}
                  >
                    {offer.calendarPriceColorDescription}
                  </span>
                </div>
              </Col>
            );
            iconLegendComponents.push(
              <Col
                className="col-12"
                key={'iconLegend_' + offer.name + '_ ' + offer.calendarIconDescription}
              >
                <div className="d-flex align-items-center">
                  <img
                    className={'meg_deal-finder-date-picker_offer_icon mr-1'}
                    src={offer?.calendarIcon?.small?.url}
                    title={offer?.calendarIcon?.small?.subTitle ?? 'Offer Icon'}
                    alt={offer?.calendarIcon?.small?.altText ?? 'Offer Icon'}
                  />
                  <span className="meg_deal-finder-date-picker_offer_icon-legend">
                    {offer?.calendarIconDescription}
                  </span>
                </div>
              </Col>
            );
          });

          return [...priceLegendComponents, ...iconLegendComponents];
        })()}
      </Row>
    );
  };

  const handleChange = (newStartDate: string | undefined, newEndDate: string | undefined) => {
    if (onChange) {
      onChange(newStartDate ?? undefined, newEndDate ?? undefined);
    }
  };

  return (
    <Row
      className={`meg_deal-finder-date-picker_container ${
        isDateRangeOnly2Days() && 'two-day-range'
      }`}
    >
      <Col>
        <DateRangeInput
          dataTestId="dealFinder_serviceSearch_selectDate"
          startDate={startDate}
          endDate={endDate}
          minDate={minDay}
          minDateRangeSpan={minDateRangeSpan !== undefined ? minDateRangeSpan : 1} //NOSONAR
          maxDateRangeSpan={maxDateRangeSpan}
          seasons={seasons}
          datePickerRef={datePickerRef}
          monthsShown={1}
          selectsDisabledDaysInRange
          customDivider={<span>-</span>}
          popoverRef={popoverRef}
          popoverTargetRef={dateEditRef as RefObject<HTMLElement>}
          dateRangeInputRef={dateRangeInputRef}
          stateFromPropsOnPopoverOpen
          useLocalSelectedDatesForInRangeCheck
          onChange={handleChange}
          withColorCodedAvailability={
            // hotelModel?.metaInfo?.contentByMetaType?.AVAILABILITY_CALENDAR?.content
            //   ?.isAvailabilityCalendarEnabled as boolean | undefined
            false
          }
          highlightDatesInformation={highlightDatesInformation}
          searchParams={searchParams}
          selectsRange
          renderInline
          customRenderDayContents={availabilityPackage ? renderDayContents : undefined}
          onMonthChange={date => {
            if (onMonthChange) {
              onMonthChange(date);
            }
          }}
          customFilterDate={availabilityPackage ? customFilterDate : undefined}
          currentlyVisibleMonth={currentlyVisibleMonth}
          withHeader={true}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled
          }: ReactDatePickerCustomHeaderProps) => (
            <div className={'headerContainer'}>
              <button
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                type="button"
                aria-label={t(Keys.previousMonth)}
              >
                {'<'}
              </button>

              <span className="react-datepicker__current-month">
                {date.toLocaleString(i18next.language ?? localStorage.getItem('i18nextLng') ?? '', {
                  month: 'long',
                  year: 'numeric'
                })}
              </span>

              <button
                onClick={increaseMonth}
                disabled={false}
                type="button"
                aria-label={t(Keys.nextMonth)}
              >
                {'>'}
              </button>
            </div>
          )}
        />
        <Row>
          <Col className="meg_deal-finder-date-picker-price_info">{t(Keys.priceInformation)}</Col>
          <Col>{renderLegends()}</Col>
        </Row>
        {isDateNotValid || isDateRangeNotValid ? (
          <Row className="pt-2">
            <Col>
              <Error
                message={
                  isDateNotValid
                    ? t(Keys.dateValidationErrorMessage)
                    : t(Keys.dateRangeNotSelectedErrorMessage)
                }
              />
            </Col>
          </Row>
        ) : (
          <> </>
        )}
      </Col>
    </Row>
  );
};

export default DealFinderDatePicker;
